import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Menu, X } from 'lucide-react';

/* ─── Nav Items ──────────────────────────────────────────────────────── */
const NAV_ITEMS = [
  { name: 'Familias', section: 'familias' },
  { name: 'Hospitales', section: 'hospitales' },
  { name: 'Integración', section: 'integracion' },
  { name: 'Contacto', href: '/contacto' },
];

/* ─── Shared Floating Navbar ─────────────────────────────────────────── */
export default function LandingNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [navHovered, setNavHovered] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHome = location.pathname === '/';

  const handleNavClick = (item: typeof NAV_ITEMS[0]) => {
    setMobileMenuOpen(false);

    // Route link (e.g. /contacto)
    if (item.href) {
      navigate(item.href);
      return;
    }

    // Section scroll — same page
    if (item.section && isHome) {
      const el = document.getElementById(item.section);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Section scroll — different page → navigate home, then scroll
    if (item.section && !isHome) {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(item.section!);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  };

  return (
    <>
      {/* ── Desktop ──────────────────────────────────────────────────── */}
      <motion.nav
        onMouseLeave={() => setNavHovered(null)}
        className="fixed inset-x-0 top-5 z-[60] mx-auto hidden w-full max-w-4xl items-center justify-between rounded-full bg-white/80 backdrop-blur-xl px-2.5 py-2 shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ring-slate-200/60 lg:flex"
      >
        {/* Logo */}
        <div className="relative z-20 flex-shrink-0 cursor-pointer pl-3" onClick={() => navigate('/')}>
          <img src="/Finales/Hospital Status Logo extendido.png" alt="Hospital Status" className="h-8 object-contain hover:opacity-80 transition-opacity" />
        </div>

        {/* Nav Items — centered */}
        <div className="flex flex-1 items-center justify-center gap-0.5">
          {NAV_ITEMS.map((item, idx) => {
            const isActive = item.href ? location.pathname === item.href : false;
            return (
              <button
                key={item.name}
                onMouseEnter={() => setNavHovered(idx)}
                onClick={() => handleNavClick(item)}
                className={`relative px-4 py-2 text-sm font-medium transition-colors hover:text-med-navy ${isActive ? 'text-med-navy' : 'text-med-navy/60'}`}
              >
                {navHovered === idx && (
                  <motion.div
                    layoutId="nav-hover-pill"
                    className="absolute inset-0 h-full w-full rounded-full bg-med-light/60"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-20 font-heading">{item.name}</span>
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate('/contacto')}
          className="relative z-20 flex-shrink-0 rounded-full bg-med-navy px-6 py-2.5 text-sm font-bold text-white shadow-[0px_-2px_0px_0px_rgba(255,255,255,0.15)_inset] transition-all hover:bg-med-accent hover:shadow-[0_0_20px_rgba(91,101,220,0.3)]"
        >
          Agendar una demo
        </button>
      </motion.nav>

      {/* ── Mobile ───────────────────────────────────────────────────── */}
      <motion.nav
        animate={{ borderRadius: mobileMenuOpen ? '16px' : '9999px' }}
        className="fixed inset-x-0 top-4 z-[60] mx-4 flex flex-col bg-white/80 backdrop-blur-xl px-4 py-3 shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ring-slate-200/60 lg:hidden"
      >
        <div className="flex w-full items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <img src="/Finales/Hospital Status Logo extendido.png" alt="Hospital Status" className="h-7 object-contain" />
          </div>
          {mobileMenuOpen ? (
            <X className="w-5 h-5 text-med-navy cursor-pointer" onClick={() => setMobileMenuOpen(false)} />
          ) : (
            <Menu className="w-5 h-5 text-med-navy cursor-pointer" onClick={() => setMobileMenuOpen(true)} />
          )}
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-1 pt-4 pb-2">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item)}
                    className="w-full text-left px-4 py-2.5 text-sm font-medium text-med-navy/70 rounded-xl hover:bg-med-light/40 transition-colors font-heading"
                  >
                    {item.name}
                  </button>
                ))}
                <div className="border-t border-slate-100 mt-2 pt-3">
                  <Link
                    to="/staff"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-med-navy/50 rounded-xl hover:bg-med-light/40 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Activity className="w-4 h-4" />
                    Acceso Médico
                  </Link>
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/contacto'); }}
                    className="w-full mt-2 rounded-xl bg-med-navy px-6 py-3 text-sm font-bold text-white shadow-[0px_-2px_0px_0px_rgba(255,255,255,0.15)_inset] transition-all hover:bg-med-accent"
                  >
                    Agendar una demo
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
